import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { formatDate                             } from '@angular/common';

import { NotificationService, OverviewTableService, ReferralsCountersService, ReferralsService, WebsocketService } from '@shared/services';
import {
  getStatusText,
  TableBodyEntryModel,
  TableHeaderEntryModel
} from '@shared/models';
import { Referee } from '@shared/factories';

@Component({
  templateUrl: '../../table-entries.component.html',
  selector:     'referrals-table',
  styleUrls: ['./referrals-table.component.sass'],
  host: { class: 'am-flex-column bg-white b-radius-wide overflow' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ReferralsTableComponent {
  @Input() rows:         Referee[];
  @Input() paginationId: string;
  @Input() loading:      boolean;

  @Output() headerCallback = new EventEmitter<any>();
  @Output() updateData     = new EventEmitter<any>();

  constructor(
    private referralsCounter:     ReferralsCountersService,
    private referralsService:     ReferralsService,
    private overviewTableService: OverviewTableService,
    private websocketService:     WebsocketService,
    private notificationService:  NotificationService
  ) { }

  headerCallbackHandler(res) {
    this.headerCallback.emit(res);
  }

  getTableClass(): string {
    return 'referrals-list-wrapper';
  }

  getOptionalValue(field: string): boolean {
    return this[field] || null;
  }

  prepareTableHeader(): TableHeaderEntryModel[] {
    return [
      { title: 'Mitarbeiter',  class: 'name-cell'                                 },
      { title: 'Eingang',      class: 'create-cell',  sort_by: ['created_at']     },
      { title: 'Bewerber',     class: 'refer-cell'                                },
      { title: 'E-Mail',       class: 'email-cell'                                },
      { title: 'Mobil (Bew.)', class: 'phone-cell'                                },
      { title: 'Jet Status',   class: 'status-cell'                               },
      { title: 'Archiviert',   class: 'archive-cell', pageOnly: ['archive']       },
      { title: 'Aktion',       class: 'action-cell',  pageOnly: ['referral-list'] }
    ].filter(h => h.pageOnly ? h.pageOnly.find(p => p === this.paginationId) : true);
  }

  private preparePreload(): TableBodyEntryModel[] {
    return new Array<TableBodyEntryModel>(10).fill({
      cells: [
        this.overviewTableService.getExternalEmployeeCellPlaceholder(),
        { xs_label: ' ', class: 'create-cell',  value: ' ' },
        { xs_label: ' ', class: 'refer-cell', children: [
          { class: 'am-flex-center width-xl   height-xl loading-animate' },
          { class: 'am-flex-1      width-full height-xl loading-animate' }
        ]},
        { xs_label: ' ', class: 'email-cell',   value: ' ' },
        { xs_label: ' ', class: 'phone-cell',   value: ' ' },
        { xs_label: ' ', class: 'status-cell',  buttons: [{ class: 'custom height-30 width-105 '}]      },
        { xs_label: ' ', class: 'archive-cell', value: ' ',                 pageOnly: ['archive']       },
        { class: 'action-cell', buttons: [{ class: 'width-36 height-36' }], pageOnly: ['referral-list'] }
      ].filter(c => c.pageOnly && c.pageOnly.filter(p => p).length ? c.pageOnly.filter(p => p).find(p => p === this.paginationId) : true)
    });
  }

  prepareTableBody(): TableBodyEntryModel[] {
    if (this.loading) return this.preparePreload();
    return this.rows.map((row: Referee): TableBodyEntryModel => ({
      cells: [
        this.overviewTableService.getExternalEmployeeCell(row.referrer),
        { xs_label: 'Eingang',      class: 'create-cell',   value: formatDate(row.createdAt, 'dd.MM.yyyy', 'de') },
        { xs_label: 'Bewerber',     class: 'refer-cell',    children: [
          { class: `am-flex-align am-flex-justify gap-xxs referee-name-block ${row.refereeVerifiedAt ? 'color-green' : ''}`,  children: [
            { class: `am-flex-center circle-l icon-tick ${row.refereeVerifiedAt ? '' : 'hide'}` },
            { class: 'referee-name am-flex-1', value: row.referee.nameReverse                   },
          ]},
          { skip: !row.refereeVerifiedAt,
            class: `web-tooltip bottom-left short green`,
            children: [{
              class: "tooltip-title",
              value: 'Daten bestätigt'
            }]
          }
        ]},
        { xs_label: 'E-Mail',       class: 'email-cell',    value: row.referee.email        || '-'               },
        { xs_label: 'Mobil (Bew.)', class: 'phone-cell',    value: row.referee.phone_number || '-'               },
        { xs_label: 'Jet Status',   class: 'status-cell',   children: [
          { class: `hiring-state ${row.hiringState}`, value: getStatusText(row.hiringState) },
        ]},
        { xs_label: 'Archiviert',   class: 'archive-cell',  value: formatDate(row.archivedAt, 'dd.MM.yyyy', 'de'), pageOnly: ['archive'] },
        { xs_label: null,           class: 'action-cell',                                                          pageOnly: ['referral-list'],
          buttons: [
            { class:   'font-small-icon tooltip-hover blue-thin-border ma',
              icon:    'icon-archive am-flex-center',
              tooltip: 'Archivieren',
              click: () => this.manualArchive(row)
            },
            {
              class: `font-small-icon tooltip-hover p-s blue-thin-border ${row.jetIdentifier ? '' : 'hide-i events-none' }`,
              label: 'J',
              tooltip: 'JET',
              click: () => this.redirectToJet(row)
            }
          ]
        }
      ].filter(c => c.pageOnly && c.pageOnly.filter(p => p).length ? c.pageOnly.filter(p => p).find(p => p === this.paginationId) : true)
    }));
  }

  private manualArchive(referee: Referee): void {
    this.notificationService.confirm('Bestätigung', 'Möchten Sie diese Empfehlung archivieren ?', () => {
      this.notificationService.wait();
      this.referralsService.archive(referee.id)
        .then(() => this.refreshData())
        .catch(error => this.notificationService.alert(error.message || error))
        .finally(() => this.notificationService.close());
    });
  }

  private redirectToJet(referee: Referee): void {
    window.open(`https://jet.tempton-connect.de/applicant/${referee.jetIdentifier}/edit`, '_blank');
  }

  private refreshData(): void {
    this.updateData.emit();
    this.reloadCounters();
  }

  private reloadCounters(): void {
    if (!this.websocketService.wsOnline()) this.referralsCounter.reloadCounters();
  }

}
